/**
 * DATA codecs for every command of the DGI protocol annex SECEF BF ST 1.0
 * (§4.4): C1h, C2h, 2Bh, C3h, C0h, 31h, 33h, 35h, 36h, 38h.
 *
 * For each command:
 * - `encode…Request` / `decode…Response` are the SFE side;
 * - `decode…Request` / `encode…Response` are the MCF side (simulator, tests).
 *
 * Encoders validate every field and throw `SecefFieldError`. Request decoders
 * throw `SecefFieldError` on a syntax error. Response decoders return a
 * discriminated union when the command has an error form (`E:…`).
 *
 * @module @efacture/mcf-protocol/secef-commands
 */
import { DEFAULT_SECEF_DIALECT } from "./secef-dialect.js";
import { SecefFieldError, encodeSecefText, escapeSecefText, formatHundredthsCompact, formatHundredthsDgiMcf, formatHundredthsFixed, formatSecefAmountCompact, formatSecefAmountFixed, formatSecefQuantity, parseSecefDecimal, parseSecefInteger, splitSecefFields, toHundredths, unescapeSecefText, } from "./secef-fields.js";
// =============================================================================
// Wire codes
// =============================================================================
export const SECEF_TAX_GROUPS = Object.freeze([
    "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P",
]);
export const SECEF_PSVB_GROUPS = Object.freeze(["A", "B", "C", "D"]);
export const SECEF_SALES_INVOICE_TYPES = Object.freeze(["FV", "FT", "EV", "ET"]);
export const SECEF_CREDIT_NOTE_TYPES = Object.freeze(["FA", "EA"]);
export const SECEF_CREDIT_NOTE_NATURES = Object.freeze(["COR", "RAN", "RAM", "RRR"]);
export const SECEF_CLIENT_TYPES = Object.freeze(["CC", "PM", "PP", "PC"]);
export const SECEF_PRICE_MODES = Object.freeze(["TTC", "HT"]);
export const SECEF_PAYMENT_METHODS = Object.freeze(["V", "C", "M", "D", "E", "A"]);
export const SECEF_TAXPAYER_FIELDS = Object.freeze(["I0", "I1", "I2", "I3", "I4"]);
export const SECEF_COMMENT_LINES = Object.freeze(["A", "B", "C", "D", "E", "F", "G", "H"]);
/** 36h CONTENU limit. */
export const SECEF_EXTRA_INFO_MAX_CHARS = 255;
function oneOf(values, value, field) {
    if (!values.includes(value)) {
        throw new SecefFieldError(`${field} invalide : « ${value} »`, field);
    }
    return value;
}
function toBuffer(text) {
    return Buffer.from(text, "utf8");
}
function nonEmpty(value, field) {
    if (value.length === 0)
        throw new SecefFieldError(`${field} obligatoire`, field);
    return value;
}
function parseErrorReply(text) {
    if (text === "E")
        return { ok: false, message: "" };
    const match = /^E(?:\s+E)?\s*:\s*([\s\S]*)$/.exec(text);
    return match ? { ok: false, message: match[1] ?? "" } : null;
}
function encodeErrorReply(message, dialect, prefix = "E:") {
    return toBuffer(prefix + escapeSecefText(message, dialect));
}
const DATE_TIME = /^\d{14}$/;
const C1H_FIELD_COUNT = 11 + SECEF_TAX_GROUPS.length + SECEF_PSVB_GROUPS.length;
export function encodeMcfStatusRequest() {
    return Buffer.alloc(0);
}
function formatDgiMcfAmount(value, field) {
    return formatHundredthsDgiMcf(toHundredths(value, field));
}
/**
 * `fixed` writes DMV and the rates with two decimals, as in the §4.4 example;
 * `compact` drops the decimals of whole values (`18`, `0`); `dgi-mcf` writes
 * them as the DGI MCF does (`18.0`, `0.2`).
 */
export function encodeMcfStatusResponse(status, dialect = DEFAULT_SECEF_DIALECT, numbers = "fixed") {
    const amount = numbers === "fixed" ? formatSecefAmountFixed : numbers === "compact" ? formatSecefAmountCompact : formatDgiMcfAmount;
    const last = status.lastInvoice;
    const fields = [
        escapeSecefText(status.machineId, dialect),
        escapeSecefText(status.ifu, dialect),
        status.dateTime,
        String(status.totalCounter),
        String(status.salesCounter),
        String(status.creditNoteCounter),
        last ? last.dateTime : "",
        last ? escapeSecefText(last.type, dialect) : "",
        last ? escapeSecefText(last.secefCode, dialect) : "",
        last ? escapeSecefText(last.number, dialect) : "",
        last ? amount(last.amount, "DMV") : "",
        ...SECEF_TAX_GROUPS.map((g) => amount(status.taxRates[g], `TAX${g}`)),
        ...SECEF_PSVB_GROUPS.map((g) => amount(status.psvbRates[g], `PSVB${g}`)),
    ];
    return toBuffer(fields.join(","));
}
export function decodeMcfStatusResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const f = splitSecefFields(data.toString("utf8"), dialect);
    if (f.length !== C1H_FIELD_COUNT) {
        throw new SecefFieldError(`C1h : ${C1H_FIELD_COUNT} champs attendus, ${f.length} reçus`, "C1h");
    }
    const at = (i) => f[i] ?? "";
    const taxRates = {};
    SECEF_TAX_GROUPS.forEach((g, i) => {
        taxRates[g] = parseSecefDecimal(at(11 + i), `TAX${g}`);
    });
    const psvbRates = {};
    SECEF_PSVB_GROUPS.forEach((g, i) => {
        psvbRates[g] = parseSecefDecimal(at(27 + i), `PSVB${g}`);
    });
    const hasLast = at(6) !== "" || at(8) !== "";
    return {
        machineId: at(0),
        ifu: at(1),
        dateTime: at(2),
        totalCounter: parseSecefInteger(at(3), "TC"),
        salesCounter: parseSecefInteger(at(4), "FVC"),
        creditNoteCounter: parseSecefInteger(at(5), "FRC"),
        lastInvoice: hasLast
            ? {
                dateTime: at(6),
                type: at(7),
                secefCode: at(8),
                number: at(9),
                amount: at(10) === "" ? 0 : parseSecefDecimal(at(10), "DMV"),
            }
            : null,
        taxRates,
        psvbRates,
    };
}
export function encodeServerStatusRequest() {
    return Buffer.alloc(0);
}
export function encodeServerStatusResponse(status, dialect = DEFAULT_SECEF_DIALECT) {
    const fields = [String(status.sent), String(status.available), status.lastConnection];
    if (status.lastError !== undefined && status.lastError !== "") {
        fields.push(escapeSecefText(status.lastError, dialect));
    }
    return toBuffer(fields.join(","));
}
export function decodeServerStatusResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const f = splitSecefFields(data.toString("utf8"), dialect);
    if (f.length < 3 || f.length > 4) {
        throw new SecefFieldError(`C2h : 3 ou 4 champs attendus, ${f.length} reçus`, "C2h");
    }
    const base = {
        sent: parseSecefInteger(f[0] ?? "", "EC"),
        available: parseSecefInteger(f[1] ?? "", "DC"),
        lastConnection: f[2] ?? "",
    };
    return f.length === 4 ? { ...base, lastError: f[3] ?? "" } : base;
}
// =============================================================================
// 2Bh — taxpayer information
// =============================================================================
export function encodeTaxpayerInfoRequest(field) {
    return toBuffer(oneOf(SECEF_TAXPAYER_FIELDS, field, "OPT"));
}
export function decodeTaxpayerInfoRequest(data) {
    return oneOf(SECEF_TAXPAYER_FIELDS, data.toString("utf8"), "OPT");
}
export function encodeTaxpayerInfoResponse(value, dialect = DEFAULT_SECEF_DIALECT) {
    return toBuffer(escapeSecefText(value, dialect));
}
export function decodeTaxpayerInfoResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    return unescapeSecefText(data.toString("utf8"), dialect);
}
/** Bound on a C3h text field; the frame limit is the effective one. */
const CLIENT_FIELD_MAX_CHARS = 200;
/** `null` sends no data: a cash client (CC). */
export function encodeClientInfoRequest(client, dialect = DEFAULT_SECEF_DIALECT) {
    if (client === null)
        return Buffer.alloc(0);
    const text = (value, field) => encodeSecefText(value ?? "", field, CLIENT_FIELD_MAX_CHARS, dialect);
    return toBuffer([
        oneOf(SECEF_CLIENT_TYPES, client.type, "CTYPE"),
        text(client.ifu, "CIFU"),
        text(client.name, "CNOM"),
        text(client.address, "CADD"),
        text(client.phone, "CTEL"),
        text(client.email, "CEML"),
        text(client.rccm, "CRCCM"),
    ].join(","));
}
/** Returns `null` when the request carries no data. */
export function decodeClientInfoRequest(data, dialect = DEFAULT_SECEF_DIALECT) {
    if (data.length === 0)
        return null;
    const f = splitSecefFields(data.toString("utf8"), dialect, dialect.trimFieldSpaces);
    if (f.length !== 7) {
        throw new SecefFieldError(`C3h : 7 champs attendus, ${f.length} reçus`, "C3h");
    }
    const pick = (key, value) => value ? { [key]: value } : {};
    return {
        type: oneOf(SECEF_CLIENT_TYPES, f[0] ?? "", "CTYPE"),
        ...pick("ifu", f[1]),
        ...pick("name", f[2]),
        ...pick("address", f[3]),
        ...pick("phone", f[4]),
        ...pick("email", f[5]),
        ...pick("rccm", f[6]),
    };
}
export function encodeClientInfoResponse(reply, dialect = DEFAULT_SECEF_DIALECT) {
    return reply.ok ? toBuffer("R") : encodeErrorReply(reply.message, dialect);
}
export function decodeClientInfoResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    if (text === "R")
        return { ok: true };
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, message: unescapeSecefText(error.message, dialect) };
    throw new SecefFieldError(`C3h : réponse inattendue « ${text} »`, "RC");
}
function isCreditNoteType(type) {
    return SECEF_CREDIT_NOTE_TYPES.includes(type);
}
const RN_MAX_CHARS = 64;
export function encodeOpenInvoiceRequest(request, dialect = DEFAULT_SECEF_DIALECT) {
    const type = oneOf([...SECEF_SALES_INVOICE_TYPES, ...SECEF_CREDIT_NOTE_TYPES], request.invoiceType, "VT/RT");
    const fields = [
        nonEmpty(encodeSecefText(request.operatorId, "OPID", 16, dialect), "OPID"),
        nonEmpty(encodeSecefText(request.operatorName, "OPN", 32, dialect), "OPN"),
        nonEmpty(encodeSecefText(request.ifu, "IFU", 32, dialect), "IFU"),
        type,
    ];
    if (isCreditNoteType(type)) {
        if (!request.creditNote) {
            throw new SecefFieldError("RR et RN obligatoires pour une facture d'avoir", "RR");
        }
        fields.push(oneOf(SECEF_CREDIT_NOTE_NATURES, request.creditNote.nature, "RR"), nonEmpty(encodeSecefText(request.creditNote.reference, "RN", RN_MAX_CHARS, dialect), "RN"));
    }
    else if (request.creditNote) {
        throw new SecefFieldError("RR et RN réservés aux factures d'avoir", "RR");
    }
    fields.push(oneOf(SECEF_PRICE_MODES, request.priceMode, "PMODE"), nonEmpty(encodeSecefText(request.isf, "ISF", 32, dialect), "ISF"), nonEmpty(encodeSecefText(request.invoiceNumber, "FN", 32, dialect), "FN"));
    if (request.psvbGroup !== undefined) {
        fields.push(oneOf(SECEF_PSVB_GROUPS, request.psvbGroup, "PSVB"));
    }
    return toBuffer(fields.join(","));
}
export function decodeOpenInvoiceRequest(data, dialect = DEFAULT_SECEF_DIALECT) {
    const f = splitSecefFields(data.toString("utf8"), dialect, dialect.trimFieldSpaces);
    const type = oneOf([...SECEF_SALES_INVOICE_TYPES, ...SECEF_CREDIT_NOTE_TYPES], f[3] ?? "", "VT/RT");
    const credit = isCreditNoteType(type);
    const base = credit ? 6 : 4;
    if (f.length !== base + 3 && f.length !== base + 4) {
        throw new SecefFieldError(`C0h : ${base + 3} ou ${base + 4} champs attendus, ${f.length} reçus`, "C0h");
    }
    const text = (i, field, max) => {
        const value = nonEmpty(f[i] ?? "", field);
        if ([...value].length > max)
            throw new SecefFieldError(`${field} dépasse ${max} caractères`, field);
        return value;
    };
    const psvb = f[base + 3];
    return {
        operatorId: text(0, "OPID", 16),
        operatorName: text(1, "OPN", 32),
        ifu: text(2, "IFU", 32),
        invoiceType: type,
        ...(credit
            ? {
                creditNote: {
                    nature: oneOf(SECEF_CREDIT_NOTE_NATURES, f[4] ?? "", "RR"),
                    reference: text(5, "RN", RN_MAX_CHARS),
                },
            }
            : {}),
        priceMode: oneOf(SECEF_PRICE_MODES, f[base] ?? "", "PMODE"),
        isf: text(base + 1, "ISF", 32),
        invoiceNumber: text(base + 2, "FN", 32),
        ...(psvb !== undefined && psvb !== "" ? { psvbGroup: oneOf(SECEF_PSVB_GROUPS, psvb, "PSVB") } : {}),
    };
}
export function encodeOpenInvoiceResponse(reply, dialect = DEFAULT_SECEF_DIALECT) {
    if (!reply.ok)
        return encodeErrorReply(reply.message, dialect);
    const c = reply.client;
    return toBuffer([
        String(reply.typeCounter),
        String(reply.totalCounter),
        ...[c.ifu, c.name, c.address, c.phone, c.email, c.rccm].map((v) => escapeSecefText(v, dialect)),
    ].join(","));
}
export function decodeOpenInvoiceResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, message: unescapeSecefText(error.message, dialect) };
    const f = splitSecefFields(text, dialect);
    if (f.length !== 8) {
        throw new SecefFieldError(`C0h : 8 champs attendus, ${f.length} reçus`, "C0h");
    }
    return {
        ok: true,
        typeCounter: parseSecefInteger(f[0] ?? "", "FC"),
        totalCounter: parseSecefInteger(f[1] ?? "", "TC"),
        client: {
            ifu: f[2] ?? "",
            name: f[3] ?? "",
            address: f[4] ?? "",
            phone: f[5] ?? "",
            email: f[6] ?? "",
            rccm: f[7] ?? "",
        },
    };
}
const TAB = "\t";
const LF = "\n";
function wireArticleType(type, dialect) {
    if (type === "IMPSER" || type === "IMPSERV")
        return dialect.importedServiceCode;
    return oneOf(["LOCBIE", "LOCSER", "IMPBIE"], type, "ITYPE");
}
function assertPositive(value, field) {
    if (!(value > 0))
        throw new SecefFieldError(`${field} doit être supérieur à zéro`, field);
}
export function encodeItemRequest(item, dialect = DEFAULT_SECEF_DIALECT) {
    const name = nonEmpty(encodeSecefText(item.name, "NOM", 64, dialect), "NOM");
    const code = item.code !== undefined && item.code !== "" ? LF + encodeSecefText(item.code, "DESC", 24, dialect) : "";
    const group = oneOf(SECEF_TAX_GROUPS, item.taxGroup, "TAX");
    if (!(item.taxRate >= 0))
        throw new SecefFieldError("TR doit être positif ou nul", "TR");
    assertPositive(item.amount, "MON");
    assertPositive(item.unitPrice, "PR");
    let price = formatSecefAmountCompact(item.unitPrice, "PR");
    if (item.quantity !== undefined && item.quantity !== 1) {
        assertPositive(item.quantity, "QT");
        price += `*${formatSecefQuantity(item.quantity, "QT")}`;
    }
    if (item.specificTax) {
        const ts = item.specificTax;
        if (!(ts.total >= 0))
            throw new SecefFieldError("TS doit être positif ou nul", "TS");
        const rate = ts.rate.kind === "percent"
            ? `${formatSecefAmountCompact(ts.rate.percent, "TSR")}%`
            : formatSecefAmountCompact(ts.rate.amount, "TSR");
        price += `;T${rate},${formatSecefAmountCompact(ts.total, "TS")}`;
        if (ts.description !== undefined && ts.description !== "") {
            price += `,${encodeSecefText(ts.description, "TSDESC", 16, dialect)}`;
        }
    }
    let change = "";
    if (item.priceChange) {
        assertPositive(item.priceChange.originalPrice, "PRORIG");
        change =
            TAB +
                `${formatSecefAmountCompact(item.priceChange.originalPrice, "PRORIG")},` +
                encodeSecefText(item.priceChange.description, "PRDESC", 24, dialect);
    }
    const typeTax = wireArticleType(item.articleType, dialect) +
        (dialect.itemTypeTaxComma ? "," : "") +
        group +
        formatSecefAmountFixed(item.taxRate, "TR") +
        "%" +
        formatSecefAmountCompact(item.amount, "MON");
    return toBuffer(name + code + TAB + typeTax + TAB + price + change);
}
const NUMBER = "\\d+(?:\\.\\d+)?";
const TYPE_TAX = new RegExp(`^(LOCBIE|LOCSER|IMPBIE|IMPSERV|IMPSER),?([A-P])(${NUMBER})%(${NUMBER})$`);
const PRICE = new RegExp(`^(${NUMBER})(?:\\*(${NUMBER}))?(?:;T(${NUMBER})(%?),(${NUMBER})(?:,([\\s\\S]*))?)?$`);
const PRICE_CHANGE = new RegExp(`^(${NUMBER}),([\\s\\S]*)$`);
/** Accepts ITYPE and TAX with or without the comma between them. */
export function decodeItemRequest(data, dialect = DEFAULT_SECEF_DIALECT) {
    const parts = data.toString("utf8").split(TAB);
    if (parts.length !== 3 && parts.length !== 4) {
        throw new SecefFieldError(`31h : 3 ou 4 blocs séparés par TAB attendus, ${parts.length} reçus`, "31h");
    }
    const [head = "", typeTax = "", priceText = "", changeText] = parts;
    const lf = head.indexOf(LF);
    const name = unescapeSecefText(lf < 0 ? head : head.slice(0, lf), dialect);
    const code = lf < 0 ? undefined : unescapeSecefText(head.slice(lf + 1), dialect);
    nonEmpty(name, "NOM");
    if ([...name].length > 64)
        throw new SecefFieldError("NOM dépasse 64 caractères", "NOM");
    if (code !== undefined && [...code].length > 24)
        throw new SecefFieldError("DESC dépasse 24 caractères", "DESC");
    const tt = TYPE_TAX.exec(typeTax);
    if (!tt)
        throw new SecefFieldError(`31h : ITYPE,TAX TR%MON illisible « ${typeTax} »`, "ITYPE");
    const pr = PRICE.exec(priceText);
    if (!pr)
        throw new SecefFieldError(`31h : PR[*QT][;T…] illisible « ${priceText} »`, "PR");
    const amount = Number(tt[4]);
    const unitPrice = Number(pr[1]);
    const quantity = pr[2] !== undefined ? Number(pr[2]) : undefined;
    assertPositive(amount, "MON");
    assertPositive(unitPrice, "PR");
    if (quantity !== undefined)
        assertPositive(quantity, "QT");
    let specificTax;
    if (pr[3] !== undefined) {
        const rateValue = Number(pr[3]);
        const description = pr[6] !== undefined ? unescapeSecefText(pr[6], dialect) : undefined;
        if (description !== undefined && [...description].length > 16) {
            throw new SecefFieldError("TSDESC dépasse 16 caractères", "TSDESC");
        }
        specificTax = {
            rate: pr[4] === "%" ? { kind: "percent", percent: rateValue } : { kind: "per-unit", amount: rateValue },
            total: Number(pr[5]),
            ...(description !== undefined ? { description } : {}),
        };
    }
    let priceChange;
    if (changeText !== undefined) {
        const pc = PRICE_CHANGE.exec(changeText);
        if (!pc)
            throw new SecefFieldError(`31h : PRORIG,PRDESC illisible « ${changeText} »`, "PRORIG");
        const description = unescapeSecefText(pc[2] ?? "", dialect);
        if ([...description].length > 24)
            throw new SecefFieldError("PRDESC dépasse 24 caractères", "PRDESC");
        priceChange = { originalPrice: Number(pc[1]), description };
    }
    return {
        name,
        ...(code !== undefined ? { code } : {}),
        articleType: tt[1],
        taxGroup: tt[2],
        taxRate: Number(tt[3]),
        amount,
        unitPrice,
        ...(quantity !== undefined ? { quantity } : {}),
        ...(specificTax ? { specificTax } : {}),
        ...(priceChange ? { priceChange } : {}),
    };
}
export function encodeItemResponse(reply, dialect = DEFAULT_SECEF_DIALECT, numbers = "annex") {
    if (!reply.ok)
        return encodeErrorReply(reply.message, dialect);
    const amount = numbers === "annex" ? formatSecefAmountFixed : formatDgiMcfAmount;
    return toBuffer([
        amount(reply.total, "MV"),
        amount(reply.taxable, "MH"),
        amount(reply.vat, "MT"),
        amount(reply.specificTax, "TS"),
    ].join(","));
}
export function decodeItemResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, message: unescapeSecefText(error.message, dialect) };
    const f = splitSecefFields(text, dialect);
    if (f.length !== 4)
        throw new SecefFieldError(`31h : 4 champs attendus, ${f.length} reçus`, "31h");
    return {
        ok: true,
        total: parseSecefDecimal(f[0] ?? "", "MV"),
        taxable: parseSecefDecimal(f[1] ?? "", "MH"),
        vat: parseSecefDecimal(f[2] ?? "", "MT"),
        specificTax: parseSecefDecimal(f[3] ?? "", "TS"),
    };
}
const SUBTOTAL_FIELD_COUNT = 1 + 2 * SECEF_TAX_GROUPS.length + 2;
export function encodeSubtotalRequest() {
    return Buffer.alloc(0);
}
/** Writes zero as `0` and any other amount with two decimals. */
function formatSubtotalAmount(value, field) {
    const hundredths = toHundredths(value, field);
    return hundredths === 0 ? "0" : formatHundredthsFixed(hundredths);
}
export function encodeSubtotalResponse(subtotal, numbers = "annex") {
    const amount = numbers === "annex" ? formatSubtotalAmount : formatDgiMcfAmount;
    return toBuffer([
        amount(subtotal.total, "MV"),
        ...SECEF_TAX_GROUPS.map((g) => amount(subtotal.groupTotals[g], `MV${g}`)),
        ...SECEF_TAX_GROUPS.map((g) => amount(subtotal.groupVat[g], `MT${g}`)),
        amount(subtotal.psvb, "MPSVB"),
        amount(subtotal.specificTax, "MTS"),
    ].join(","));
}
export function decodeSubtotalResponse(data) {
    const f = data.toString("utf8").split(",");
    if (f.length !== SUBTOTAL_FIELD_COUNT) {
        throw new SecefFieldError(`33h : ${SUBTOTAL_FIELD_COUNT} champs attendus, ${f.length} reçus`, "33h");
    }
    const groupTotals = {};
    const groupVat = {};
    SECEF_TAX_GROUPS.forEach((g, i) => {
        groupTotals[g] = parseSecefDecimal(f[1 + i] ?? "", `MV${g}`);
        groupVat[g] = parseSecefDecimal(f[17 + i] ?? "", `MT${g}`);
    });
    return {
        total: parseSecefDecimal(f[0] ?? "", "MV"),
        groupTotals,
        groupVat,
        psvb: parseSecefDecimal(f[33] ?? "", "MPSVB"),
        specificTax: parseSecefDecimal(f[34] ?? "", "MTS"),
    };
}
/** `null` sends no data: cash for the remaining amount. */
export function encodePaymentRequest(payment) {
    if (payment === null)
        return Buffer.alloc(0);
    const method = oneOf(SECEF_PAYMENT_METHODS, payment.method, "PA");
    if (payment.amount === undefined)
        return toBuffer(method);
    assertPositive(payment.amount, "MT");
    return toBuffer(method + formatSecefAmountCompact(payment.amount, "MT"));
}
export function decodePaymentRequest(data) {
    const text = data.toString("utf8");
    if (text.length === 0)
        return null;
    const match = new RegExp(`^([A-Z])(${NUMBER})?$`).exec(text);
    if (!match)
        throw new SecefFieldError(`35h : PA[MT] illisible « ${text} »`, "PA");
    const method = oneOf(SECEF_PAYMENT_METHODS, match[1] ?? "", "PA");
    if (match[2] === undefined)
        return { method };
    const amount = Number(match[2]);
    assertPositive(amount, "MT");
    return { method, amount };
}
/** `dgi-mcf`: a zero MP is written `0`, any other as `formatHundredthsDgiMcf` (`R0`, `R-400.0`, `R50.0`). */
export function encodePaymentResponse(reply, dialect = DEFAULT_SECEF_DIALECT, numbers = "annex") {
    const balance = (value) => {
        const hundredths = toHundredths(value, "MP");
        if (numbers === "annex")
            return formatHundredthsCompact(hundredths);
        return hundredths === 0 ? "0" : formatHundredthsDgiMcf(hundredths);
    };
    if (reply.ok)
        return toBuffer(`R${balance(reply.balance)}`);
    if (reply.balance !== null)
        return toBuffer(`E${balance(reply.balance)}`);
    return encodeErrorReply(reply.message, dialect);
}
export function decodePaymentResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    const match = /^([RE])(-?\d+(?:\.\d+)?)$/.exec(text);
    if (match) {
        const balance = Number(match[2]);
        return match[1] === "R" ? { ok: true, balance } : { ok: false, balance, message: "" };
    }
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, balance: null, message: unescapeSecefText(error.message, dialect) };
    throw new SecefFieldError(`35h : réponse inattendue « ${text} »`, "RC");
}
function wireExtraInfoField(field, dialect) {
    if (field === "REGIMP")
        return dialect.taxRegimeLid;
    return oneOf([...SECEF_COMMENT_LINES, "COMBAN", "SERIMP"], field, "LID");
}
export function encodeExtraInfoRequest(request, dialect = DEFAULT_SECEF_DIALECT) {
    const lid = wireExtraInfoField(request.field, dialect);
    if (!("content" in request))
        return toBuffer(lid);
    return toBuffer(`${lid},${encodeSecefText(request.content, "CONTENU", SECEF_EXTRA_INFO_MAX_CHARS, dialect)}`);
}
/** Accepts `REGIMP` with or without a leading space. */
export function decodeExtraInfoRequest(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    const comma = text.indexOf(",");
    const lidText = comma < 0 ? text : text.slice(0, comma);
    const lid = lidText.trim() === "REGIMP" ? "REGIMP" : lidText;
    const field = oneOf([...SECEF_COMMENT_LINES, "REGIMP", "COMBAN", "SERIMP"], lid, "LID");
    if (comma < 0)
        return { field };
    const content = unescapeSecefText(text.slice(comma + 1), dialect);
    if ([...content].length > SECEF_EXTRA_INFO_MAX_CHARS) {
        throw new SecefFieldError(`CONTENU dépasse ${SECEF_EXTRA_INFO_MAX_CHARS} caractères`, "CONTENU");
    }
    return { field, content };
}
export function encodeExtraInfoResponse(reply, dialect = DEFAULT_SECEF_DIALECT) {
    if (!reply.ok)
        return encodeErrorReply(reply.message, dialect, "E E:");
    return reply.mode === "read" ? toBuffer(escapeSecefText(reply.content, dialect)) : toBuffer("R");
}
/** `mode` is the kind of request sent: a read returns CONTENU, a write RC. */
export function decodeExtraInfoResponse(data, mode, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    if (mode === "read")
        return { ok: true, mode, content: unescapeSecefText(text, dialect) };
    if (text === "R")
        return { ok: true, mode };
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, message: unescapeSecefText(error.message, dialect) };
    throw new SecefFieldError(`36h : réponse inattendue « ${text} »`, "RC");
}
export function encodeCloseInvoiceRequest(request) {
    if (request.kind === "cancel")
        return toBuffer("C");
    assertPositive(request.total, "MV");
    return toBuffer(formatSecefAmountFixed(request.total, "MV"));
}
export function decodeCloseInvoiceRequest(data) {
    const text = data.toString("utf8");
    if (text === "C")
        return { kind: "cancel" };
    const total = parseSecefDecimal(text, "MV");
    assertPositive(total, "MV");
    return { kind: "certify", total };
}
/** 38h error texts as printed in the §4.4 examples. */
export const SECEF_38H_INVALID_AMOUNT = "LE MONTANT N'EST PAS VALIDE";
export const SECEF_38H_CANCELLED = "ANNULEE";
export function encodeCloseInvoiceResponse(reply, dialect = DEFAULT_SECEF_DIALECT) {
    if (!reply.ok)
        return encodeErrorReply(reply.message, dialect);
    const head = [
        String(reply.typeCounter),
        String(reply.totalCounter),
        escapeSecefText(reply.invoiceType, dialect),
        reply.dateTime,
        escapeSecefText(reply.machineId, dialect),
        escapeSecefText(reply.ifu, dialect),
    ];
    const sig = escapeSecefText(reply.secefCode, dialect);
    const fn = escapeSecefText(reply.invoiceNumber, dialect);
    const tail = dialect.field38hOrder === "example" ? [sig, fn] : sig === "" ? [fn] : [fn, sig];
    return toBuffer([...head, ...tail].join(","));
}
export function decodeCloseInvoiceResponse(data, dialect = DEFAULT_SECEF_DIALECT) {
    const text = data.toString("utf8");
    const error = parseErrorReply(text);
    if (error)
        return { ok: false, message: unescapeSecefText(error.message, dialect) };
    const f = splitSecefFields(text, dialect);
    if (f.length !== 7 && f.length !== 8) {
        throw new SecefFieldError(`38h : 7 ou 8 champs attendus, ${f.length} reçus`, "38h");
    }
    const dateTime = f[3] ?? "";
    if (!DATE_TIME.test(dateTime))
        throw new SecefFieldError(`38h : DT illisible « ${dateTime} »`, "DT");
    let secefCode = "";
    let invoiceNumber;
    if (f.length === 7) {
        invoiceNumber = f[6] ?? "";
    }
    else if (dialect.field38hOrder === "example") {
        secefCode = f[6] ?? "";
        invoiceNumber = f[7] ?? "";
    }
    else {
        invoiceNumber = f[6] ?? "";
        secefCode = f[7] ?? "";
    }
    return {
        ok: true,
        typeCounter: parseSecefInteger(f[0] ?? "", "FC"),
        totalCounter: parseSecefInteger(f[1] ?? "", "TC"),
        invoiceType: f[2] ?? "",
        dateTime,
        machineId: f[4] ?? "",
        ifu: f[5] ?? "",
        secefCode,
        invoiceNumber,
    };
}
/** Splits a MID (`NIM-ACNT`) at its last dash. */
export function splitSecefMachineId(machineId) {
    const dash = machineId.lastIndexOf("-");
    if (dash <= 0 || dash === machineId.length - 1)
        return null;
    return { nim: machineId.slice(0, dash), activationCount: machineId.slice(dash + 1) };
}
